import axios from 'axios';
import { useEffect, useCallback } from 'react';
import { ErrorMessage } from 'formik';
import { Form, Select, Input } from 'antd';
import { ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import { usePaymentDetails } from 'entities/organization/';
import { PaymentDetailsSummary } from 'widgets/Invoices/InvoiceGenerateForm.PaymentDetailsSummary';
import {
  extractFirstChar,
  extractLastChar,
  isValidIban,
} from 'shared/lib/string';
import { Link } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Switch } from '@headlessui/react';
import classNames from 'classnames';
import { useQuery } from '@tanstack/react-query';
import { useDebounce } from 'use-debounce';
import { Spinner } from 'shared/ui/Spinner';

export const useValidateIban = (iban) => {
  const [debouncedIban] = useDebounce(iban, 1000);
  const { data, isFetching } = useQuery({
    queryKey: ['validate-iban', debouncedIban],
    queryFn: async () => {
      const res = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/public/iban/validate/${debouncedIban}`
      );

      return res?.data?.result;
    },
    enabled: !!debouncedIban && isValidIban(debouncedIban),
  });

  const getBic = useCallback(() => {
    return data?.data?.bank?.bic;
  }, [data]);

  const getBankName = useCallback(() => {
    return data?.data?.bank?.bank_name;
  }, [data]);

  return {
    getBic,
    getBankName,
    isValidating: isFetching,
    validationResult: data,
  };
};

export const PaymentDetails = ({ formValues, validateForm, onValueChange }) => {
  const { data: userPaymentDetails } = usePaymentDetails();
  const { isAuthenticated } = useAuth0();
  const { getBic, getBankName, isValidating } = useValidateIban(
    formValues.iban
  );

  const validatedBankName = getBankName();
  const validatedBic = getBic();

  const paymentDetailsList = (userPaymentDetails?.result || []).filter((p) =>
    formValues.crypto_payment ? p.type === 'crypto_wallet' : p.type === 'bank'
  );

  // Utils

  const _resetPaymentDetails = () => {
    onValueChange('bank_account_name_beneficiary', '');
    onValueChange('bank_name', '');
    onValueChange('bic', '');
    onValueChange('iban', '');
    onValueChange('ach_routing_number', '');
    onValueChange('account_number', '');
    onValueChange('wallet', '');
  };

  // Renderers

  const shouldShowPaymentDetailsSelect = () => isAuthenticated;

  const shouldShowCryptoForm = () =>
    formValues.crypto_payment &&
    (formValues.paymentDetails === 'new' || !isAuthenticated);
  const shouldShowBankForm = () =>
    formValues.fiat_payment &&
    (formValues.paymentDetails === 'new' || !isAuthenticated);

  // Raw fix but for select we need to trigger validation manually in order to see correct errors right after change

  useEffect(() => {
    validateForm(formValues);
  }, [formValues.paymentDetails, formValues.token]);

  useEffect(() => {
    validatedBankName && onValueChange('bank_name', validatedBankName);
    validatedBic && onValueChange('bic', validatedBic);
  }, [onValueChange, validatedBankName, validatedBic]);

  return (
    <>
      {shouldShowPaymentDetailsSelect() && (
        <>
          <div className="col-span-8 sm:col-span-6">
            <Form.Item
              layout="vertical"
              label="Where do you want to receive your payment?"
              help={
                <span>
                  {formValues.crypto_payment
                    ? 'Find or add new wallet'
                    : 'Find or add new bank account'}

                  <Link
                    className="ml-2 underline"
                    target="_blank"
                    to="/dashboard/company/payment-details">
                    Manage Payment Details
                  </Link>
                </span>
              }>
              <Select
                size="large"
                name="paymentDetails"
                placeholder={
                  formValues.crypto_payment
                    ? 'Select Wallet'
                    : 'Select Bank Account'
                }
                onChange={(value) => {
                  onValueChange('paymentDetails', value);

                  if (value === 'new') {
                    _resetPaymentDetails();
                  } else {
                    const paymentDetail = paymentDetailsList.find(
                      (p) => p.id === value
                    )?.details;

                    if (formValues.crypto_payment) {
                      onValueChange(
                        'wallet',
                        paymentDetail?.cryptoWallet?.address || ''
                      );
                    } else {
                      onValueChange(
                        'bank_account_name_beneficiary',
                        paymentDetail?.bankAccount?.accountBeneficiary || ''
                      );
                      onValueChange(
                        'bank_name',
                        paymentDetail?.bankAccount?.bankName || ''
                      );
                      onValueChange(
                        'bic',
                        paymentDetail?.bankAccount?.bankBIC || ''
                      );
                      onValueChange(
                        'iban',
                        paymentDetail?.bankAccount?.accountIBAN || ''
                      );
                      onValueChange(
                        'ach_routing_number',
                        paymentDetail?.bankAccount?.accountACHRoutingNumber ||
                          ''
                      );
                      onValueChange(
                        'account_number',
                        paymentDetail?.bankAccount?.accountNumber || ''
                      );
                      onValueChange(
                        'bankAccountBeneficiaryType',
                        paymentDetail?.bankAccount
                          ?.bankAccountBeneficiaryType || 'business'
                      );

                      if (paymentDetail?.bankAccount?.accountACHRoutingNumber) {
                        onValueChange('isACHTransfer', true);
                      } else {
                        onValueChange('isACHTransfer', false);
                      }
                    }
                  }
                }}
                value={formValues.paymentDetails}
                options={[
                  ...paymentDetailsList.map((paymentDetail) => ({
                    value: paymentDetail.id,
                    label: (
                      <>
                        {paymentDetail.name}{' '}
                        {formValues.crypto_payment && (
                          <span className="dark:text-gray-400">
                            (
                            {extractFirstChar(
                              paymentDetail.details.cryptoWallet.address,
                              6
                            )}{' '}
                            ...{' '}
                            {extractLastChar(
                              paymentDetail.details.cryptoWallet.address,
                              6
                            )}
                            )
                          </span>
                        )}
                      </>
                    ),
                  })),
                  {
                    value: 'new',
                    label: formValues.crypto_payment
                      ? '+ Add New Wallet'
                      : '+ Add New Bank Account',
                  },
                ]}
              />
            </Form.Item>
            {!formValues.paymentDetails && (
              <ErrorMessage
                name="paymentDetails"
                component="div"
                className="mt-2 text-xs italic text-rose-500"
              />
            )}
          </div>

          <PaymentDetailsSummary
            selectedPaymentDetailsId={formValues.paymentDetails}
            userPaymentDetailsList={userPaymentDetails?.result}
          />

          {formValues.paymentDetails === 'new' && (
            <div className="col-span-8 sm:col-span-6">
              <Form.Item
                label={
                  formValues.crypto_payment ? 'Wallet Name' : 'Your reference'
                }
                layout="vertical"
                id="paymentDetailsName"
                help={
                  formValues.crypto_payment
                    ? 'This will be your reference the next time you want to use this wallet'
                    : 'This will be your reference the next time you want to use this bank details'
                }>
                <Input
                  name={'paymentDetailsName'}
                  size="large"
                  onChange={(evt) => {
                    onValueChange('paymentDetailsName', evt.target.value);
                  }}
                  value={formValues.paymentDetailsName}
                />
              </Form.Item>
            </div>
          )}

          {formValues.token === 'USD' && !formValues.isACHTransfer && (
            <div className="col-span-8 flex flex-col sm:col-span-6  pb-2 text-left text-sm font-medium">
              <span className="text-yellow-300">
                {' '}
                <span className="font-medium text-yellow-300">
                  <ExclamationTriangleIcon className="mb-0.5 mr-0.5 inline h-3 w-3" />
                </span>
                Currency Mismatch Warning
              </span>

              <span
                className={`col-span-2 text-xs font-normal text-slate-400 `}>
                You’re requesting a USD payment to an EU-based IBAN. This may
                result in unexpected FX fees or poor exchange rates applied by
                the receiving bank. For best results, consider requesting the
                payment in EUR to match the IBAN's native currency.
              </span>
            </div>
          )}
        </>
      )}

      {shouldShowCryptoForm() && (
        <div className="col-span-6 sm:col-span-6 lg:col-span-6">
          <Form.Item label={'Wallet Address *'} layout="vertical" id="wallet">
            <Input
              placeholder="0x.."
              name={'wallet'}
              size="large"
              onChange={(evt) => {
                onValueChange('wallet', evt.target.value);
              }}
              value={formValues.wallet}
            />
          </Form.Item>

          <ErrorMessage
            name="wallet"
            component="div"
            className="mt-2 text-xs italic text-rose-500"
          />
        </div>
      )}

      {shouldShowBankForm() && (
        <>
          {formValues.token === 'USD' && (
            <div className="sm:col-span-full">
              <Switch.Group as="div" className="items-left flex ">
                <Switch.Label
                  as="span"
                  className="mr-5 text-sm font-medium leading-6 text-secondary-200"
                  passive>
                  ACH Transfer
                </Switch.Label>
                <Switch
                  id="requestee.paymentDetails.isACHTransfer"
                  checked={formValues.isACHTransfer}
                  onChange={() => {
                    onValueChange('isACHTransfer', !formValues.isACHTransfer);
                  }}
                  className={classNames(
                    formValues.isACHTransfer ? 'bg-indigo-600' : 'bg-gray-500',
                    'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2'
                  )}>
                  <span
                    aria-hidden="true"
                    className={classNames(
                      formValues.isACHTransfer
                        ? 'translate-x-5'
                        : 'translate-x-0',
                      'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                    )}
                  />
                </Switch>
              </Switch.Group>
            </div>
          )}

          {formValues.token === 'USD' && formValues.isACHTransfer && (
            <>
              <div className="col-span-6 sm:col-span-6 lg:col-span-3">
                <Form.Item
                  label="Routing Number *"
                  layout="vertical"
                  id="ach_routing_number"
                  help="The routing number for ACH transfers">
                  <Input
                    name="ach_routing_number"
                    size="large"
                    onChange={(evt) => {
                      onValueChange('ach_routing_number', evt.target.value);
                    }}
                    value={formValues.ach_routing_number}
                  />
                </Form.Item>
                <ErrorMessage
                  name="ach_routing_number"
                  component="div"
                  className="mt-2 text-xs italic text-rose-500"
                />
              </div>
              <div className="col-span-6 sm:col-span-6 lg:col-span-3">
                <Form.Item
                  label="Account Number *"
                  layout="vertical"
                  id="account_number"
                  help="The account number for ACH transfers">
                  <Input
                    name="account_number"
                    size="large"
                    onChange={(evt) => {
                      onValueChange('account_number', evt.target.value);
                    }}
                    value={formValues.account_number}
                  />
                </Form.Item>
                <ErrorMessage
                  name="account_number"
                  component="div"
                  className="mt-2 text-xs italic text-rose-500"
                />
              </div>
            </>
          )}

          {((formValues.token === 'USD' && !formValues.isACHTransfer) ||
            formValues.token !== 'USD') && (
            <>
              <div className="col-span-6 sm:col-span-6 lg:col-span-3">
                <Form.Item
                  label={
                    <div className="flex items-center">
                      IBAN *
                      {isValidating && (
                        <Spinner className="border-2 h-4 w-4 ml-2" />
                      )}
                    </div>
                  }
                  layout="vertical"
                  id="iban"
                  help={'The International Bank Account Number.'}>
                  <Input
                    name={'iban'}
                    size="large"
                    onChange={(evt) => {
                      onValueChange('iban', evt.target.value);
                    }}
                    value={formValues.iban}
                  />
                </Form.Item>
                <ErrorMessage
                  name="iban"
                  component="div"
                  className="mt-2 text-xs italic text-rose-500"
                />
              </div>
              <div className="col-span-6 sm:col-span-6 lg:col-span-3">
                <Form.Item
                  label={'BIC / SWIFT Code *'}
                  layout="vertical"
                  id="bic"
                  help={'The BIC/SWIFT code of your bank.'}>
                  <Input
                    name={'bic'}
                    size="large"
                    onChange={(evt) => {
                      onValueChange('bic', evt.target.value);
                    }}
                    value={formValues.bic}
                  />
                </Form.Item>
                <ErrorMessage
                  name="bic"
                  component="div"
                  className="mt-2 text-xs italic text-rose-500"
                />
              </div>
            </>
          )}

          <div className="col-span-6 sm:col-span-6 lg:col-span-3">
            <Form.Item
              label={'Account Beneficiary Name *'}
              layout="vertical"
              id="bank_account_name_beneficiary"
              help={'The name of the bank account holder.'}>
              <Input
                name={'bank_account_name_beneficiary'}
                size="large"
                onChange={(evt) => {
                  onValueChange(
                    'bank_account_name_beneficiary',
                    evt.target.value
                  );
                }}
                value={formValues.bank_account_name_beneficiary}
              />
            </Form.Item>
            <ErrorMessage
              name="bank_account_name_beneficiary"
              component="div"
              className="mt-2 text-xs italic text-rose-500"
            />
          </div>

          <div className="col-span-6 sm:col-span-6 lg:col-span-3">
            <Form.Item
              label={'Bank Name *'}
              layout="vertical"
              id="bank_name"
              help={'The name of your bank.'}>
              <Input
                name={'bank_name'}
                size="large"
                onChange={(evt) => {
                  onValueChange('bank_name', evt.target.value);
                }}
                value={formValues.bank_name}
              />
            </Form.Item>

            <ErrorMessage
              name="bank_name"
              component="div"
              className="mt-2 text-xs italic text-rose-500"
            />
          </div>
        </>
      )}
    </>
  );
};
